import { Box, MenuItem, menuItemClasses, SelectChangeEvent } from '@mui/material';
import { format as originalFormat, isBefore, isToday } from 'date-fns';
import { memo, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';

import DownIcon from '../../../assets/icons/Icon_ArrowDown.svg?react';
import { ResourceType, useQueryGetGetFlights } from '../../../backend';
import {
    Header,
    LeftComponentsHeader,
    MiddleComponentsHeader,
    RightComponentsHeader,
} from '../../navigation/Header/Header.style';
import { Nav } from '../../navigation/Nav/Nav';
import { PageTitle } from '../../navigation/Nav/PageTitle';
import { SelectedDate } from '../../navigation/SelectedDate/SelectedDate';
import { Clock } from '../../navigation/ToggleUTCLocal/Clock';
import { ganttDayFormat } from '../RmsContent/Gantt/gantt.constants';
import { useRmsGanttState, useRmsGanttValue } from '../RmsContent/RmsContext';
import { ResourcesAllocationButton } from './ResourcesAllocationButton';
import { StyledSelect } from './RmsHeader.style';
import { ViewModeButton } from './ViewMode/ViewModeButton';

export const RmsHeader = memo(function RmsHeader({ enableGanttAutoScroll }: { enableGanttAutoScroll: () => void }) {
    const day = useRmsGanttValue('day');
    const [resourceType, setResourceType] = useRmsGanttState('resourceType');
    const { data: ganttDto } = useQueryGetGetFlights({
        selectedDay: originalFormat(day, ganttDayFormat),
        selectedResourceType: resourceType,
    });
    const turnarounds = ganttDto?.turnarounds;
    const nbArrivals = useMemo(() => turnarounds?.filter(t => t.considerArrivalFlight).length, [turnarounds]);
    const nbDepartures = useMemo(() => turnarounds?.filter(t => t.considerDepartureFlight).length, [turnarounds]);
    const nbTurnarounds = nbArrivals + nbDepartures;
    const nbUpdateAllocations = useRmsGanttValue('nbUpdateAllocations');

    return (
        <Header>
            <LeftComponentsHeader>
                <Nav>
                    <PageTitle>RMS</PageTitle>
                </Nav>
                <StyledSelect
                    value={resourceType}
                    disabled={nbUpdateAllocations !== 0}
                    onChange={(e: SelectChangeEvent<ResourceType>) => setResourceType(ResourceType[e.target.value])}
                    IconComponent={DownIcon}
                    MenuProps={{
                        PaperProps: {
                            sx: {
                                [`& .${menuItemClasses.root}`]: {
                                    color: 'white',
                                },
                            },
                        },
                    }}
                >
                    {Object.values(ResourceType).map((resourceType, index) => (
                        <MenuItem key={index} value={resourceType}>
                            {resourceType}
                        </MenuItem>
                    ))}
                </StyledSelect>
                <ResourcesAllocationButton disabled={nbUpdateAllocations !== 0 || isBefore(day, new Date())} />
            </LeftComponentsHeader>
            <MiddleComponentsHeader>
                <SelectedDate disabled={nbUpdateAllocations !== 0} />
            </MiddleComponentsHeader>
            <RightComponentsHeader>
                <Box sx={{ justifyContent: 'space-between', display: 'flex', width: '100%', alignItems: 'center' }}>
                    <Box sx={{ fontFamily: 'Roboto', fontSize: 'medium' }}>
                        <FormattedMessage
                            id={'rms.nb.mvt'}
                            values={{
                                turnarounds: nbTurnarounds || 0,
                                arrivals: nbArrivals ?? 0,
                                departures: nbDepartures ?? 0,
                            }}
                        />
                    </Box>
                    <ViewModeButton />
                    <Clock onClickCallback={isToday(day) ? enableGanttAutoScroll : undefined} />
                </Box>
            </RightComponentsHeader>
        </Header>
    );
});
